import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {StorageService} from "../../../services/storage/storage.service";
import {OrgDto} from "../../dto/org.dto";
import {UrlBuilder} from "../../item/url.item";
import {WrapperDto} from "../../dto/wrapper.dto";
import {map} from "rxjs/operators";

import {Observable} from "rxjs";
import {VehicleEngineDto} from "../../dto/vehicle/vehicle-engine.dto";
import {VehicleTypeDto} from "../../dto/vehicle/vehicle-type.dto";
import {VehicleManufacturerDto} from "../../dto/vehicle/vehicle-manufacturer.dto";
import {VehicleModelDto} from "../../dto/vehicle/vehicle-model.dto";
import {VehicleVariantDto} from "../../dto/vehicle/vehicle-variant.dto";

@Injectable({providedIn: 'root'})
export class VehicleRepo {

  private readonly engineStoragePrefix = 'vehicle:engine:all:';
  private readonly typeStoragePrefix = 'vehicle:type:all:';
  private readonly manufacturerStoragePrefix = 'vehicle:manufacturer:';
  private readonly modelStoragePrefix = 'vehicle:model:';
  private readonly variantStoragePrefix = 'vehicle:variant:';

  private readonly engineRead = '/v1/org/{orgId}/vehicle/engine';
  private readonly typeRead = '/v1/org/{orgId}/vehicle/type';
  private readonly manufacturerRead = '/v1/org/{orgId}/vehicle/type/{typeId}/manufacturer';
  private readonly modelRead = '/v1/org/{orgId}/vehicle/manufacturer/{manufacturerId}/model';
  private readonly variantRead = '/v1/org/{orgId}/vehicle/model/{modelId}/variant';

  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);

  public getEngines(org: OrgDto): Observable<WrapperDto<VehicleEngineDto>> {
    const url = UrlBuilder.builder().addPath(this.engineRead).addParam('orgId', org.id).build();
    return this.storage.cache(this.engineStoragePrefix + org.id, () =>
      this.http.get<WrapperDto<VehicleEngineDto>>(url.getUrl(), url.getOptions())
    ).pipe(map((res) => new WrapperDto(res)));
  }

  public getTypes(org: OrgDto): Observable<WrapperDto<VehicleTypeDto>> {
    const url = UrlBuilder.builder().addPath(this.typeRead).addParam('orgId', org.id).build();
    return this.storage.cache(this.typeStoragePrefix + org.id, () =>
      this.http.get<WrapperDto<VehicleTypeDto>>(url.getUrl(), url.getOptions())
    ).pipe(map((res) => new WrapperDto(res)));
  }

  public getManufacturers(org: OrgDto, typeId: string): Observable<WrapperDto<VehicleManufacturerDto>> {
    const url = UrlBuilder.builder().addPath(this.manufacturerRead)
      .addParam('orgId', org.id).addParam('typeId', typeId).build();
    return this.storage.cache(this.manufacturerStoragePrefix + typeId + ':' + org.id, () =>
      this.http.get<WrapperDto<VehicleManufacturerDto>>(url.getUrl(), url.getOptions())
    ).pipe(map((res) => new WrapperDto(res)));
  }

  public getModels(org: OrgDto, manufacturerId: string): Observable<WrapperDto<VehicleModelDto>> {
    const url = UrlBuilder.builder().addPath(this.modelRead)
      .addParam('orgId', org.id).addParam('manufacturerId', manufacturerId).build();
    return this.storage.cache(this.modelStoragePrefix + manufacturerId + ':' + org.id, () =>
      this.http.get<WrapperDto<VehicleModelDto>>(url.getUrl(), url.getOptions())
    ).pipe(map((res) => new WrapperDto(res)));
  }

  public getVariants(org: OrgDto, modelId: string): Observable<WrapperDto<VehicleVariantDto>> {
    const url = UrlBuilder.builder().addPath(this.variantRead)
      .addParam('orgId', org.id).addParam('modelId', modelId).build();
    return this.storage.cache(this.variantStoragePrefix + modelId + ':' + org.id, () =>
      this.http.get<WrapperDto<VehicleVariantDto>>(url.getUrl(), url.getOptions())
    ).pipe(map((res) => new WrapperDto(res)));
  }

  public clearAllCaches(): void {
    this.storage.cacheRemoveByPrefix(this.engineStoragePrefix);
    this.storage.cacheRemoveByPrefix(this.typeStoragePrefix);
    this.storage.cacheRemoveByPrefix(this.manufacturerStoragePrefix);
    this.storage.cacheRemoveByPrefix(this.modelStoragePrefix);
    this.storage.cacheRemoveByPrefix(this.variantStoragePrefix);
  }
}
